/**
 * ============================================================
 * KEJJA OS — src/lib/libraryApi.ts
 * ============================================================
 */

import { api } from '@/lib/api';

// ============================================================
// Types
// ============================================================

export type LibraryDocType =
  | 'note' | 'id_document' | 'receipt' | 'medical' | 'school'
  | 'certificate' | 'contract' | 'photo' | 'link' | 'other';

export interface LibraryFolder {
  id: string;
  homeId: string;
  ownerUserId: string | null;
  parentFolderId: string | null;
  name: string;
  color: string;
  icon: string | null;
  sortOrder: number;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface LibraryItem {
  id: string;
  homeId: string;
  ownerUserId: string | null;
  folderId: string | null;
  docType: LibraryDocType;
  title: string;
  body: string | null;
  tags: string[];
  metadata: Record<string, unknown>;
  isFavorite: boolean;
  isArchived: boolean;
  createdByUserId: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

export interface LibraryAttachment {
  id: string;
  homeId: string;
  itemId: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  storagePath: string;
  uploadedByUserId: string | null;
  uploadedAt: string;
  deletedAt: string | null;
}

export interface ItemWithAttachments extends LibraryItem {
  attachments: LibraryAttachment[];
}

export interface LibraryPulse {
  counts: {
    itemsTotal: number;
    itemsShared: number;
    itemsPersonal: number;
    foldersShared: number;
    foldersPersonal: number;
    favoritesTotal: number;
    attachmentsTotal: number;
  };
  recent: Array<{
    id: string;
    title: string;
    docType: string;
    ownerUserId: string | null;
    updatedAt: string;
    folderId: string | null;
  }>;
}

// ============================================================
// Payloads
// ============================================================

export interface FolderCreatePayload {
  name: string;
  color?: string;
  icon?: string | null;
  ownerUserId?: string | null;
  parentFolderId?: string | null;
  sortOrder?: number;
}
export type FolderUpdatePayload = Partial<FolderCreatePayload>;

export interface ItemCreatePayload {
  title: string;
  body?: string | null;
  docType?: LibraryDocType;
  tags?: string[];
  metadata?: Record<string, unknown>;
  folderId?: string | null;
  ownerUserId?: string | null;
  isFavorite?: boolean;
}
export type ItemUpdatePayload = Partial<ItemCreatePayload> & { isArchived?: boolean };

export interface ItemListParams {
  scope?: 'all' | 'shared' | 'personal';
  folderId?: string | null;
  docType?: LibraryDocType;
  tags?: string[];
  q?: string;
  favoritesOnly?: boolean;
  includeArchived?: boolean;
}

// ============================================================
// API
// ============================================================

interface LibraryApi {
  pulse: (homeId: string) => Promise<LibraryPulse>;

  listFolders: (homeId: string, scope?: 'all' | 'shared' | 'personal') => Promise<{ folders: LibraryFolder[] }>;
  createFolder: (homeId: string, body: FolderCreatePayload) => Promise<{ folder: LibraryFolder }>;
  updateFolder: (homeId: string, folderId: string, body: FolderUpdatePayload) => Promise<{ folder: LibraryFolder }>;
  deleteFolder: (homeId: string, folderId: string) => Promise<void>;

  listItems: (homeId: string, params?: ItemListParams) => Promise<{ items: LibraryItem[] }>;
  getItem: (homeId: string, itemId: string) => Promise<{ item: ItemWithAttachments }>;
  createItem: (homeId: string, body: ItemCreatePayload) => Promise<{ item: LibraryItem }>;
  updateItem: (homeId: string, itemId: string, body: ItemUpdatePayload) => Promise<{ item: LibraryItem }>;
  deleteItem: (homeId: string, itemId: string) => Promise<void>;

  listTags: (homeId: string) => Promise<{ tags: Array<{ tag: string; count: number }> }>;

  uploadAttachment: (homeId: string, itemId: string, file: File) => Promise<{ attachment: LibraryAttachment }>;
  downloadUrl: (homeId: string, attachmentId: string) => string;
  deleteAttachment: (homeId: string, attachmentId: string) => Promise<void>;
}

const base = (homeId: string) => `/homes/${homeId}/library`;

function buildItemQuery(p?: ItemListParams): string {
  if (!p) return '';
  const qs = new URLSearchParams();
  if (p.scope) qs.set('scope', p.scope);
  if (p.folderId === null) qs.set('folderId', 'null');
  else if (p.folderId) qs.set('folderId', p.folderId);
  if (p.docType) qs.set('docType', p.docType);
  if (p.tags?.length) qs.set('tags', p.tags.join(','));
  if (p.q) qs.set('q', p.q);
  if (p.favoritesOnly) qs.set('favoritesOnly', 'true');
  if (p.includeArchived) qs.set('includeArchived', 'true');
  const s = qs.toString();
  return s ? `?${s}` : '';
}

export const libraryApi: LibraryApi = {
  pulse: (homeId) => api.get<LibraryPulse>(`${base(homeId)}/pulse`),

  listFolders: (homeId, scope) =>
    api.get<{ folders: LibraryFolder[] }>(`${base(homeId)}/folders${scope ? `?scope=${scope}` : ''}`),
  createFolder: (homeId, body) =>
    api.post<{ folder: LibraryFolder }>(`${base(homeId)}/folders`, body),
  updateFolder: (homeId, folderId, body) =>
    api.patch<{ folder: LibraryFolder }>(`${base(homeId)}/folders/${folderId}`, body),
  deleteFolder: (homeId, folderId) =>
    api.delete<void>(`${base(homeId)}/folders/${folderId}`),

  listItems: (homeId, params) =>
    api.get<{ items: LibraryItem[] }>(`${base(homeId)}/items${buildItemQuery(params)}`),
  getItem: (homeId, itemId) =>
    api.get<{ item: ItemWithAttachments }>(`${base(homeId)}/items/${itemId}`),
  createItem: (homeId, body) =>
    api.post<{ item: LibraryItem }>(`${base(homeId)}/items`, body),
  updateItem: (homeId, itemId, body) =>
    api.patch<{ item: LibraryItem }>(`${base(homeId)}/items/${itemId}`, body),
  deleteItem: (homeId, itemId) =>
    api.delete<void>(`${base(homeId)}/items/${itemId}`),

  listTags: (homeId) =>
    api.get<{ tags: Array<{ tag: string; count: number }> }>(`${base(homeId)}/tags`),

  uploadAttachment: async (homeId, itemId, file) => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`/api/v1${base(homeId)}/items/${itemId}/attachments`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (!res.ok) {
      let msg = `HTTP ${res.status}`;
      try { const j = await res.json(); msg = j.error ?? msg; } catch {}
      throw new Error(msg);
    }
    return await res.json();
  },
  downloadUrl: (homeId, attachmentId) =>
    `/api/v1${base(homeId)}/attachments/${attachmentId}/download`,
  deleteAttachment: (homeId, attachmentId) =>
    api.delete<void>(`${base(homeId)}/attachments/${attachmentId}`),
};
